/**
 * @file feed-event-meta.ts
 * @description Реестр видов событий ленты заказа: вид → значок, тон и текст
 * из структурных полей записи (phase19.31, uiux-design.md §4.1, Н3/Н5;
 * контракт cabinet-api-contract.md §9).
 *
 * Текст строится из полей (`stage`, `payment`, `notification`…), а не из
 * заголовка сервера с кодами: «Стадия: Согласование доставки → Ожидание
 * оплаты», «Оплата получена — 175,00 ₽, наличные». Новый вид события —
 * новая запись реестра, не ветка. Неизвестный вид — нейтральная строка с
 * заголовком сервера (если он есть).
 */

import {
  ArrowRight,
  Ban,
  Banknote,
  Check,
  CircleCheck,
  CircleDot,
  ListChecks,
  Mail,
  MessageSquare,
  MessagesSquare,
  PackageCheck,
  PackagePlus,
  Phone,
  ReceiptText,
  Send,
  StickyNote,
  Truck,
  Undo2,
  Users,
  type LucideIcon,
} from "lucide-react";

import {
  notificationChannelsLine,
  notificationFailed,
  notificationReached,
  notificationTitle,
} from "./feed-notification";
import type { FeedEntry, FeedTone } from "./feed-types";
import {
  currencySymbol,
  formatMoney,
  PAYMENT_METHOD_INLINE_LABEL,
  RETURN_TYPE_LABEL,
  SHIPMENT_CARRIER_LABEL,
  type OrderFeedActivityKind,
  type OrderFeedItemDto,
} from "../../lib/api-orders";

/** Описание вида события. */
interface FeedKindMeta {
  icon: LucideIcon;
  tone: FeedTone | ((item: OrderFeedItemDto) => FeedTone);
  text: (item: OrderFeedItemDto) => string;
  /** Вторая линия вида (без комментария — его добавляет общий код). */
  detail?: (item: OrderFeedItemDto) => string | null;
  /** Комментарий уже вошёл в текст («Заказ отменён — причина»). */
  commentInText?: boolean;
  /** Заметка стадии уже вошла в текст (причина отмены). */
  stageNoteInText?: boolean;
}

/** «175,00 ₽, наличные» — что известно о платеже. */
function paymentTail(item: OrderFeedItemDto, withMethod: boolean): string | null {
  const p = item.payment;
  if (!p) return null;
  const money = p.amount ? `${formatMoney(p.amount)} ${currencySymbol(p.currency)}` : null;
  const method = withMethod && p.method ? PAYMENT_METHOD_INLINE_LABEL[p.method] : null;
  const tail = [money, method].filter(Boolean).join(", ");
  return tail || null;
}

/** «Оплата получена — 175,00 ₽, наличные»; без данных — только начало. */
function withTail(head: string, tail: string | null): string {
  return tail ? `${head} — ${tail}` : head;
}

/** Перевозчик словом: самовывоз — строчными, названия компаний — как есть. */
function carrierText(item: OrderFeedItemDto): string | null {
  const carrier = item.shipment?.carrier;
  if (!carrier) return null;
  return carrier === "PICKUP" ? "самовывоз" : SHIPMENT_CARRIER_LABEL[carrier];
}

/** «Отгрузка O-12 · трек 1032…» — вторая линия отгрузки. */
function shipmentDetail(item: OrderFeedItemDto): string | null {
  const s = item.shipment;
  if (!s) return null;
  const parts = [s.number ? `Отгрузка ${s.number}` : null, s.trackNumber ? `трек ${s.trackNumber}` : null];
  return parts.filter(Boolean).join(" · ") || null;
}

/** Запись, написанную человеком: «Звонок: договорились на завтра». */
function humanRecord(prefix: string | null, fallback: string): Pick<FeedKindMeta, "text" | "detail"> {
  return {
    text: (item) => {
      const title = item.note?.title.trim();
      if (!title) return fallback;
      return prefix ? `${prefix}: ${title}` : title;
    },
    detail: (item) => item.note?.content?.trim() || null,
  };
}

/** Реестр видов записей журнала (`ActivityKind`). */
export const FEED_ACTIVITY_META: Record<OrderFeedActivityKind, FeedKindMeta> = {
  ORDER_CREATED: { icon: PackagePlus, tone: "progress", text: () => "Заказ создан" },
  ORDER_CONFIRMED: { icon: Check, tone: "progress", text: () => "Заказ подтверждён" },
  ORDER_COMPLETED: { icon: CircleCheck, tone: "success", text: () => "Заказ завершён" },
  ORDER_CANCELLED: {
    icon: Ban,
    tone: "muted",
    text: (item) => withTail("Заказ отменён", item.comment?.trim() || null),
    commentInText: true,
  },
  PAYMENT_RECEIVED: { icon: Banknote, tone: "success", text: (item) => withTail("Оплата получена", paymentTail(item, true)) },
  FISCAL_RECEIPT_PUNCHED: { icon: ReceiptText, tone: "neutral", text: (item) => withTail("Пробит чек", paymentTail(item, false)) },
  SHIPMENT_SENT: { icon: Truck, tone: "progress", text: (item) => withTail("Заказ отгружен", carrierText(item)), detail: shipmentDetail },
  SHIPMENT_DELIVERED: {
    icon: PackageCheck,
    tone: "success",
    text: (item) => withTail("Заказ доставлен", carrierText(item)),
    detail: shipmentDetail,
  },
  RETURN_REQUESTED: {
    icon: Undo2,
    tone: "neutral",
    text: (item) => {
      const r = item.return;
      const head = r?.number ? `Запрошен возврат ${r.number}` : "Запрошен возврат";
      return withTail(head, r?.type ? RETURN_TYPE_LABEL[r.type].toLocaleLowerCase("ru-RU") : null);
    },
  },
  RETURN_COMPLETED: {
    icon: Undo2,
    tone: "neutral",
    text: (item) => (item.return?.number ? `Возврат ${item.return.number} завершён` : "Возврат завершён"),
  },
  NOTIFICATION_SENT: {
    icon: Send,
    tone: (item) => (notificationFailed(item.notification?.channels ?? []) ? "danger" : "neutral"),
    text: (item) => {
      const channels = item.notification?.channels ?? [];
      const title = item.notification ? notificationTitle(item.notification.templateKind) : null;
      const quoted = title ? ` «${title}»` : "";
      return notificationReached(channels) || channels.length === 0
        ? `Клиенту отправлено уведомление${quoted}`
        : `Уведомление${quoted} клиенту не ушло`;
    },
    detail: (item) => notificationChannelsLine(item.notification?.channels ?? []),
  },
  NOTE: { icon: StickyNote, tone: "neutral", ...humanRecord(null, "Заметка") },
  TASK: { icon: ListChecks, tone: "neutral", ...humanRecord("Задача", "Задача") },
  CALL: { icon: Phone, tone: "neutral", ...humanRecord("Звонок", "Звонок") },
  EMAIL: { icon: Mail, tone: "neutral", ...humanRecord("Письмо", "Письмо") },
  SMS: { icon: MessageSquare, tone: "neutral", ...humanRecord("SMS", "SMS") },
  MEETING: { icon: Users, tone: "neutral", ...humanRecord("Встреча", "Встреча") },
  CHAT_MESSAGE: { icon: MessagesSquare, tone: "neutral", ...humanRecord("Переписка", "Сообщение в переписке") },
};

/** Смена стадии: создание, завершение, отмена и обычный переход. */
function stageMeta(item: OrderFeedItemDto): FeedKindMeta {
  const stage = item.stage;
  if (!stage || stage.fromName === null) return { icon: PackagePlus, tone: "progress", text: () => "Заказ создан" };
  if (stage.toKind === "CANCELLED") {
    // Причина отмены — из записи стадии, иначе из комментария.
    const note = stage.note?.trim() || null;
    return {
      icon: Ban,
      tone: "muted",
      text: () => withTail("Заказ отменён", note ?? (item.comment?.trim() || null)),
      stageNoteInText: note !== null,
      commentInText: note === null,
    };
  }
  if (stage.toKind === "COMPLETED") return { icon: CircleCheck, tone: "success", text: () => "Заказ завершён" };
  return { icon: ArrowRight, tone: "progress", text: () => `Стадия: ${stage.fromName} → ${stage.toName}` };
}

/** Описание вида; неизвестный вид — нейтральная строка с заголовком сервера. */
function metaFor(item: OrderFeedItemDto): FeedKindMeta {
  if (item.type === "stage" || item.kind === "STAGE_CHANGED") return stageMeta(item);
  const known: Partial<Record<string, FeedKindMeta>> = FEED_ACTIVITY_META;
  return known[item.kind] ?? { icon: CircleDot, tone: "neutral", text: () => item.note?.title.trim() || "Событие по заказу" };
}

/** Кто сделал: `actor: null` — «Система» (служебный пользователь, уведомления). */
export function feedActorName(item: Pick<OrderFeedItemDto, "actor">): string {
  if (!item.actor) return "Система";
  return item.actor.displayName?.trim() || "Сотрудник";
}

/** Запись сервера → строка ленты. */
export function describeOrderFeedItem(item: OrderFeedItemDto): FeedEntry {
  const meta = metaFor(item);
  const comment = meta.commentInText ? null : item.comment?.trim() || null;
  const note = meta.stageNoteInText ? null : item.stage?.note?.trim() || null;
  const detail = [meta.detail?.(item) ?? null, note, comment].filter(Boolean).join(" · ");
  return {
    id: item.id,
    at: item.at,
    icon: meta.icon,
    tone: typeof meta.tone === "function" ? meta.tone(item) : meta.tone,
    text: meta.text(item),
    detail: detail || null,
    actor: feedActorName(item),
  };
}
